/**
 * Edge Agents API Service
 * 
 * Provides types and data access for edge agents - autonomous processes
 * running on gateways that perceive, apply safety policies, and trigger actions.
 * 
 * Combines real agents from edge devices with cloud-defined agent configurations.
 */

import { api } from './api';

export type AgentType = 'Perception' | 'Policy' | 'Action' | 'Health' | 'Planning' | 'Intelligence' | 'Safety';

export type AgentStatus = 'Running' | 'Degraded' | 'Stopped' | 'Initializing' | 'Error';

export interface EdgeAgent {
  id: string;
  name: string;
  type: AgentType;
  missionId: string;
  missionName: string;
  deviceId: string;
  deviceName: string;
  status: AgentStatus;
  lastActivity: string; // ISO timestamp
  lastActivityDescription: string;
  version: string;
  uptime?: string; // e.g., "4h 32m" or ISO start timestamp
  avgDecisionLatency?: number; // in milliseconds
  config?: AgentConfig;
  source?: 'edge' | 'cloud' | 'mock'; // Where this agent data came from
  category?: string; // Agent category from registry
}

export interface AgentConfig {
  // Perception agent config
  modelName?: string;
  inputStream?: string;
  thresholds?: Record<string, number>;
  
  // Policy agent config
  policyDescription?: string;
  zones?: string[];
  restrictedZones?: string[];
  
  // Action agent config
  triggers?: string[];
  actionDescription?: string;
  cooldownSeconds?: number;
  cloudBroker?: string;
  healthEndpoint?: string;
  configPath?: string;
  
  // General config
  service?: string;
  publishInterval?: string;
  tickInterval?: number;
  dependencies?: string[];
}

export interface AgentActivity {
  timestamp: string;
  description: string;
  eventType?: string;
}

export interface AgentStats {
  totalAgents: number;
  healthyAgents: number;
  degradedAgents: number;
  stoppedAgents: number;
  avgDecisionLatency: number; // in milliseconds
}

// Map agent category to AgentType
function categoryToType(category: string): AgentType {
  switch (category?.toLowerCase()) {
    case 'perception':
      return 'Perception';
    case 'planning':
      return 'Planning';
    case 'intelligence':
      return 'Intelligence';
    case 'health':
      return 'Health';
    case 'safety':
      return 'Safety';
    default:
      return 'Perception';
  }
}

// Map agent state to AgentStatus
function stateToStatus(state: string): AgentStatus {
  switch (state?.toLowerCase()) {
    case 'running':
      return 'Running';
    case 'stopped':
      return 'Stopped';
    case 'stopping':
    case 'starting':
    case 'initialized':
      return 'Initializing';
    case 'error':
      return 'Error';
    default:
      return 'Stopped';
  }
}

/**
 * Fetch agents from edge device via cloud API proxy
 */
async function _fetchEdgeAgents(deviceId: string = 'EDGE-001'): Promise<EdgeAgent[]> {
  try {
    // Try to get agent status from edge via cloud proxy
    const response = await api.get(`/api/v1/agents/devices/${deviceId}/agents`);
    
    if (response.data && response.data.agents) {
      return response.data.agents.map((agent: any) => ({
        id: `edge-${deviceId}-${agent.name.toLowerCase().replace(/\s+/g, '_')}`,
        name: agent.name,
        type: categoryToType(agent.category),
        missionId: `mission-${agent.category}`,
        missionName: `${agent.category?.charAt(0).toUpperCase() + agent.category?.slice(1)} Pipeline`,
        deviceId: deviceId,
        deviceName: response.data.device_name || 'Edge Gateway',
        status: stateToStatus(agent.state),
        lastActivity: new Date().toISOString(),
        lastActivityDescription: agent.state === 'running' ? 'active' : agent.state,
        version: agent.version || 'v1.0.0',
        uptime: agent.uptime,
        avgDecisionLatency: agent.metrics?.latency || 5,
        source: 'edge' as const,
        category: agent.category,
        config: {
          tickInterval: agent.tick_interval,
          dependencies: agent.dependencies || [],
        },
      }));
    }
    return [];
  } catch (error) {
    console.log('Edge agents not available, using demo data');
    return [];
  }
}

// Demo agents that map to real edge services for demo purposes
const demoAgents: EdgeAgent[] = [
  // === PERCEPTION AGENTS (Real-time Detection) ===
  {
    id: 'agent-001',
    name: 'PPE_Detector_Hardware_Cam',
    type: 'Perception',
    missionId: 'mission-001',
    missionName: 'Warehouse Safety',
    deviceId: 'hardware_cam',
    deviceName: 'Hardware Camera (Qualcomm)',
    status: 'Running',
    lastActivity: new Date(Date.now() - 1000).toISOString(),
    lastActivityDescription: 'detection(person, vest=true, helmet=false)',
    version: 'yolov8-ppe:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 8,
    source: 'edge',
    config: {
      modelName: 'yolov8n-ppe + posenet',
      inputStream: 'hardware_cam',
      zones: ['Receiving_Area', 'Storage_Row_A'],
      thresholds: { confidence: 0.65, iou: 0.45 },
    },
  },
  {
    id: 'agent-002',
    name: 'PPE_Detector_RTSP_Cam',
    type: 'Perception',
    missionId: 'mission-001',
    missionName: 'Warehouse Safety',
    deviceId: 'rtsp_cam',
    deviceName: 'RTSP Camera (Lantronix)',
    status: 'Running',
    lastActivity: new Date(Date.now() - 2000).toISOString(),
    lastActivityDescription: 'detection(person, vest=true, helmet=true)',
    version: 'yolov8-ppe:v1.0.0',
    uptime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 10,
    source: 'edge',
    config: {
      modelName: 'yolov8n-ppe + posenet + reid',
      inputStream: 'rtsp://192.168.3.40',
      zones: ['Receiving_Area', 'Pedestrian_Lane'],
      thresholds: { confidence: 0.65, iou: 0.45 },
    },
  },
  {
    id: 'agent-003',
    name: 'Zone_Occupancy_Monitor',
    type: 'Perception',
    missionId: 'mission-002',
    missionName: 'Zone Management',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 500).toISOString(),
    lastActivityDescription: 'zone_update(Storage_Row_A=3, Pedestrian_Lane=1)',
    version: 'zone-occupancy:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 5,
    source: 'edge',
    config: {
      service: 'zone_occupancy_publisher',
      zones: ['Staging_A', 'Staging_B', 'Receiving_Area', 'Pedestrian_Lane'],
      publishInterval: '1s',
    },
  },
  // === POLICY AGENTS (Rule Evaluation) ===
  {
    id: 'agent-004',
    name: 'PPE_Compliance_Policy',
    type: 'Policy',
    missionId: 'mission-001',
    missionName: 'Warehouse Safety',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 3000).toISOString(),
    lastActivityDescription: 'violation_detected(Staging_B, no_helmet)',
    version: 'safety-rules:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 2,
    source: 'edge',
    config: {
      service: 'warehouse_safety_rules',
      policyDescription: 'Monitors PPE compliance. Vest required in all zones, helmet in restricted zones.',
      zones: ['Staging_A', 'Staging_B', 'Receiving_Area', 'Pedestrian_Lane'],
    },
  },
  {
    id: 'agent-005',
    name: 'Restricted_Access_Policy',
    type: 'Policy',
    missionId: 'mission-002',
    missionName: 'Zone Management',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 5000).toISOString(),
    lastActivityDescription: 'zone_breach(Pedestrian_Lane, unauthorized)',
    version: 'zone-breach:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 2,
    source: 'edge',
    config: {
      service: 'zone_breach_tracker',
      policyDescription: 'Monitors restricted zone access. Alerts on unauthorized entry to Pedestrian_Lane and Staging_B.',
      restrictedZones: ['Staging_B', 'Pedestrian_Lane'],
    },
  },
  // === ACTION AGENTS (Response & Output) ===
  {
    id: 'agent-006',
    name: 'Incident_Creator',
    type: 'Action',
    missionId: 'mission-003',
    missionName: 'Alert & Response',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 8000).toISOString(),
    lastActivityDescription: 'incident_created(INC-20251208-052146)',
    version: 'incident-agg:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 3,
    source: 'edge',
    config: {
      service: 'incident_aggregator',
      actionDescription: 'Aggregates violations and creates incidents. Applies cooldown to prevent spam.',
      cooldownSeconds: 300,
    },
  },
  {
    id: 'agent-007',
    name: 'Cloud_Sync_Agent',
    type: 'Action',
    missionId: 'mission-003',
    missionName: 'Alert & Response',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 200).toISOString(),
    lastActivityDescription: 'synced(tracks=5, incidents=1)',
    version: 'mqtt-bridge:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 1,
    source: 'edge',
    config: {
      service: 'mqtt_smart_bridge',
      actionDescription: 'Syncs edge data to cloud via MQTT. Forwards tracks, zones, and incidents.',
      cloudBroker: 'cloud.edgentik.local:1883',
    },
  },
  {
    id: 'agent-008',
    name: 'Health_Monitor',
    type: 'Action',
    missionId: 'mission-004',
    missionName: 'System Health',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 1000).toISOString(),
    lastActivityDescription: 'health_ok(cpu=45%, mem=38%, services=12/12)',
    version: 'health-api:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 1,
    source: 'edge',
    config: {
      service: 'health_api',
      actionDescription: 'Monitors edge device health. Reports CPU, memory, disk, and service status.',
      healthEndpoint: '/health',
    },
  },
  {
    id: 'agent-009',
    name: 'Config_Manager',
    type: 'Action',
    missionId: 'mission-004',
    missionName: 'System Health',
    deviceId: 'edge_device_001',
    deviceName: 'Edge Gateway',
    status: 'Running',
    lastActivity: new Date(Date.now() - 60000).toISOString(),
    lastActivityDescription: 'config_loaded(business_logic_config.yaml)',
    version: 'config-reload:v1.0.0',
    uptime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    avgDecisionLatency: 1,
    source: 'edge',
    config: {
      service: 'config_hot_reload + zone_config_receiver',
      actionDescription: 'Receives config updates from cloud and hot-reloads services without restart.',
      configPath: '/app/configs/',
    },
  },
];

// Mock activity log for detailed view
const mockActivityLog: Record<string, AgentActivity[]> = {
  'agent-001': [
    { timestamp: new Date(Date.now() - 3000).toISOString(), description: 'ppe_violation(no_helmet)', eventType: 'violation' },
    { timestamp: new Date(Date.now() - 15000).toISOString(), description: 'detection(person, vest, helmet)', eventType: 'detection' },
    { timestamp: new Date(Date.now() - 28000).toISOString(), description: 'zone_entry(Receiving_Area)', eventType: 'zone' },
    { timestamp: new Date(Date.now() - 45000).toISOString(), description: 'detection(forklift)', eventType: 'detection' },
    { timestamp: new Date(Date.now() - 60000).toISOString(), description: 'ppe_violation(no_vest)', eventType: 'violation' },
  ],
  'agent-005': [
    { timestamp: new Date(Date.now() - 12000).toISOString(), description: 'policy_check(pedestrian_lane, clear=true)', eventType: 'policy' },
    { timestamp: new Date(Date.now() - 45000).toISOString(), description: 'policy_alert(pedestrian_in_lane)', eventType: 'alert' },
    { timestamp: new Date(Date.now() - 120000).toISOString(), description: 'policy_check(pedestrian_lane, clear=true)', eventType: 'policy' },
    { timestamp: new Date(Date.now() - 180000).toISOString(), description: 'policy_check(pedestrian_lane, clear=true)', eventType: 'policy' },
    { timestamp: new Date(Date.now() - 240000).toISOString(), description: 'policy_violation(unauthorized_entry)', eventType: 'violation' },
  ],
  'agent-007': [
    { timestamp: new Date(Date.now() - 45000).toISOString(), description: 'action_executed(local_alarm, staging_b)', eventType: 'action' },
    { timestamp: new Date(Date.now() - 300000).toISOString(), description: 'action_queued(incident_ticket)', eventType: 'action' },
    { timestamp: new Date(Date.now() - 600000).toISOString(), description: 'action_executed(email_alert, supervisor)', eventType: 'action' },
    { timestamp: new Date(Date.now() - 900000).toISOString(), description: 'action_executed(local_alarm, staging_a)', eventType: 'action' },
    { timestamp: new Date(Date.now() - 1200000).toISOString(), description: 'action_skipped(duplicate_alert)', eventType: 'info' },
  ],
};

/**
 * Fetch all edge agents - returns curated demo agents that map to real services
 */
export async function fetchAgents(): Promise<EdgeAgent[]> {
  // Return our curated demo agents that accurately represent the running services
  // These map to real services: warehouse_safety_rules, zone_occupancy_publisher,
  // incident_aggregator, mqtt_smart_bridge, health_api, config_hot_reload
  return demoAgents;
}

/**
 * Fetch a single agent by ID
 */
export async function fetchAgent(agentId: string): Promise<EdgeAgent | null> {
  const agents = await fetchAgents();
  return agents.find(a => a.id === agentId) || null;
}

/**
 * Fetch agent statistics
 */
export async function fetchAgentStats(): Promise<AgentStats> {
  const agents = await fetchAgents();
  const runningAgents = agents.filter(a => a.status === 'Running');
  const avgLatency = runningAgents.length > 0
    ? runningAgents.reduce((sum, a) => sum + (a.avgDecisionLatency || 0), 0) / runningAgents.length
    : 0;
  
  return {
    totalAgents: agents.length,
    healthyAgents: agents.filter(a => a.status === 'Running').length,
    degradedAgents: agents.filter(a => a.status === 'Degraded').length,
    stoppedAgents: agents.filter(a => a.status === 'Stopped' || a.status === 'Error').length,
    avgDecisionLatency: Math.round(avgLatency),
  };
}

/**
 * Fetch recent activity for an agent
 */
export async function fetchAgentActivity(agentId: string): Promise<AgentActivity[]> {
  // Return mock activity if available, otherwise generate some
  if (mockActivityLog[agentId]) {
    return mockActivityLog[agentId];
  }
  
  // Generate generic activity for agents without specific logs
  const agent = (await fetchAgents()).find(a => a.id === agentId);
  if (!agent) return [];
  
  return [
    { 
      timestamp: agent.lastActivity, 
      description: agent.lastActivityDescription,
      eventType: 'activity',
    },
    {
      timestamp: new Date(Date.now() - 60000).toISOString(),
      description: 'heartbeat(ok)',
      eventType: 'health',
    },
    {
      timestamp: new Date(Date.now() - 120000).toISOString(),
      description: 'processing(nominal)',
      eventType: 'info',
    },
  ];
}

/**
 * Format uptime from ISO timestamp or duration string
 */
export function formatUptime(uptime?: string): string {
  if (!uptime) return 'Unknown';
  
  try {
    const startTime = new Date(uptime);
    const now = new Date();
    const diffMs = now.getTime() - startTime.getTime();
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h`;
    }
    
    return `${hours}h ${minutes}m`;
  } catch {
    return uptime;
  }
}

/**
 * Format relative time (e.g., "3s ago")
 */
export function formatRelativeTime(timestamp: string): string {
  try {
    const time = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - time.getTime();
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return 'Unknown';
  }
}

/**
 * Get agent type color for UI
 */
export function getAgentTypeColor(type: AgentType): string {
  switch (type) {
    case 'Perception':
      return '#2196F3'; // Blue
    case 'Policy':
      return '#9C27B0'; // Purple
    case 'Action':
      return '#FF9800'; // Orange
    case 'Health':
      return '#4CAF50'; // Green
    case 'Planning':
      return '#00BCD4'; // Cyan
    case 'Intelligence':
      return '#673AB7'; // Deep Purple
    case 'Safety':
      return '#F44336'; // Red
    default:
      return '#757575'; // Grey
  }
}

/**
 * Get status color for UI
 */
export function getStatusColor(status: AgentStatus): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'Running':
      return 'success';
    case 'Degraded':
      return 'warning';
    case 'Stopped':
    case 'Error':
      return 'error';
    case 'Initializing':
      return 'default';
    default:
      return 'default';
  }
}
